import { TextField, TextFieldProps } from '@mui/material'
import { useState } from 'react'
import { sanitizeInput } from 'components/request-action/sanitzeUnicode'
import { trimWhitespace, collapseWhitespace } from 'utils/whitespaceUtils'

interface SanitizedTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  onChange?: (value: string) => void
  onSanitizationError?: (hasError: boolean) => void
  maxLength?: number
  allowMultiline?: boolean
}

const illegalCharacterMessage = 'Unsupported character replaced with acceptable equivalent'

/**
 * Reusable TextField component with built-in sanitization
 * Automatically detects illegal characters and provides feedback
 */
export const SanitizedTextField = ({
  onChange,
  onSanitizationError,
  maxLength = 1000,
  allowMultiline = false,
  error: externalError = false,
  helperText: externalHelperText = '',
  ...props
}: SanitizedTextFieldProps) => {
  const [localError, setLocalError] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value

    if (maxLength && value.length > maxLength) {
      return
    }

    // Collapse consecutive Unicode whitespace during typing
    const collapsed = collapseWhitespace(value)
    const { sanitized, mapped, ignored } = sanitizeInput(collapsed)
    const hasIssue = mapped || ignored

    setLocalError(hasIssue)
    onSanitizationError?.(hasIssue)
    onChange?.(sanitized)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Trim leading/trailing Unicode whitespace on blur
    const value = e.target.value
    const trimmed = trimWhitespace(value)
    
    if (trimmed !== value) {
      onChange?.(trimmed)
    }
    
    props.onBlur?.(e)
  }

  const hasError = localError || externalError
  const helperText = localError
    ? illegalCharacterMessage
    : externalHelperText

  return (
    <TextField
      {...props}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(hasError)}
      helperText={helperText}
      inputProps={{
        ...(props.inputProps as any),
        maxLength
      }}
      multiline={allowMultiline}
    />
  )
}
